export interface SearchLocationCoordinates {
  city: string;
  region: string;
  country?: string;
  latitude: number;
  longitude: number;
}

export interface LocationSearchParameters {
  regions: string[];
  cities: string[];
}

export interface GeoSearchParameters {
  radius: number;
  latitudeAnchor: number;
  longitudeAnchor: number;
  distanceUnit: string;
}

interface ParsedLocation {
  type: "city" | "region";
  city?: string;
  region: string;
  country?: string;
}

export const GEO_DISTANCE_VALUES = [10, 20, 30, 50, 70, 90, 150, 200];

export function buildDistanceOptions(unit: string) {
  const normalizedUnit = normalizeDistanceUnit(unit);
  const label = normalizedUnit === "mi" ? "miles" : "km";
  return GEO_DISTANCE_VALUES.map((distance) => ({
    value: String(distance),
    label: `Within ${distance} ${label}`,
  }));
}

export function normalizeDistanceValue(
  value: string | number | null | undefined,
): string {
  const distance = parseDistance(value);
  return distance > 0 ? String(distance) : "";
}

export function supportsGeoDistanceSearch(values: readonly string[]): boolean {
  return values.length === 1 && parseLocation(values[0])?.type === "city";
}

export function buildLocationSearchParameters(
  values: readonly string[],
): LocationSearchParameters {
  const parsed = values
    .map(parseLocation)
    .filter((location): location is ParsedLocation => location !== null);
  const selectedRegions = new Set(
    parsed
      .filter((location) => location.type === "region")
      .map(locationKey),
  );

  const regions = new Set<string>();
  const cities = new Set<string>();
  parsed.forEach((location) => {
    regions.add(location.region);
    if (
      location.type === "city" &&
      location.city &&
      !selectedRegions.has(locationKey(location))
    ) {
      cities.add(location.city);
    }
  });

  return { regions: Array.from(regions), cities: Array.from(cities) };
}

export function resolveGeoSearchParameters({
  selectedLocations,
  availableLocations,
  distance,
  distanceUnit,
}: {
  selectedLocations: readonly string[];
  availableLocations: readonly SearchLocationCoordinates[];
  distance: string | number | null | undefined;
  distanceUnit: string | null | undefined;
}): GeoSearchParameters {
  const unit = normalizeDistanceUnit(distanceUnit);
  const radius = parseDistance(distance);
  if (!radius || !supportsGeoDistanceSearch(selectedLocations)) {
    return disabledGeoSearch(unit);
  }

  const selected = parseLocation(selectedLocations[0]);
  if (!selected?.city) {
    return disabledGeoSearch(unit);
  }
  const selectedCity = selected.city;

  const anchor = availableLocations.find(
    (location) =>
      sameText(location.city, selectedCity) &&
      sameText(location.region, selected.region) &&
      (!selected.country ||
        sameText(location.country ?? "", selected.country)),
  );
  if (!anchor || !validCoordinates(anchor.latitude, anchor.longitude)) {
    return disabledGeoSearch(unit);
  }

  return {
    radius,
    latitudeAnchor: anchor.latitude,
    longitudeAnchor: anchor.longitude,
    distanceUnit: unit,
  };
}

export function formatJobDistance(
  distance: number | null | undefined,
  distanceUnit: string | null | undefined,
): string | null {
  if (
    distance === null ||
    distance === undefined ||
    !Number.isFinite(distance) ||
    distance < 0
  ) {
    return null;
  }
  const unit = normalizeDistanceUnit(distanceUnit);
  const label = unit === "mi" ? "mi" : "km";
  const rounded =
    distance < 10 ? distance.toFixed(1) : Math.round(distance).toString();
  return `${rounded} ${label} away`;
}

function parseLocation(value: string): ParsedLocation | null {
  const normalized = value.trim();
  if (!normalized) {
    return null;
  }
  if (normalized.startsWith("region:")) {
    const [, region, country] = normalized
      .split(":")
      .map((part) => part.trim());
    return region
      ? { type: "region", region, country: country || undefined }
      : null;
  }

  const [city, region, country] = normalized
    .split(",")
    .map((part) => part.trim());
  return city && region
    ? { type: "city", city, region, country: country || undefined }
    : null;
}

function locationKey(location: ParsedLocation): string {
  return `${location.country?.toLocaleLowerCase() ?? ""}\u0000${location.region.toLocaleLowerCase()}`;
}

function parseDistance(value: string | number | null | undefined): number {
  const parsed =
    typeof value === "number"
      ? value
      : Number.parseFloat(value?.match(/[\d.]+/)?.[0] ?? "");
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function normalizeDistanceUnit(value: string | null | undefined): string {
  const normalized = value?.trim().toLocaleLowerCase();
  return normalized === "mi" || normalized === "miles" ? "mi" : "km";
}

function disabledGeoSearch(distanceUnit: string): GeoSearchParameters {
  return { radius: 0, latitudeAnchor: 0, longitudeAnchor: 0, distanceUnit };
}

function validCoordinates(latitude: number, longitude: number): boolean {
  return (
    Number.isFinite(latitude) &&
    latitude >= -90 &&
    latitude <= 90 &&
    Number.isFinite(longitude) &&
    longitude >= -180 &&
    longitude <= 180 &&
    (latitude !== 0 || longitude !== 0)
  );
}

function sameText(left: string, right: string): boolean {
  return (
    left
      .trim()
      .localeCompare(right.trim(), undefined, { sensitivity: "accent" }) === 0
  );
}
